import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { FilterAltOutlined, SearchOutlined, TranslateOutlined } from '@mui/icons-material';
import {
  FbeFrontline,
  FbeQuestion,
  FbeQuestionType,
  FbeSection,
  getLanguageDisplayName,
  LocalisedText,
} from '../../services/fbeTypes';
import { getLocalisedText, hasMeaningfulText, upsertLocalisedText } from '../../services/translationCompletion';
import RichTextField from '../RichTextField';

// ── Styled components ─────────────────────────────────────────────────────────

const SideBySideRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px',
  alignItems: 'stretch',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const ColumnHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '13px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#666',
  marginBottom: '8px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
    '&:first-of-type': { display: 'block' },
  },
}));

const SourceBox = styled(Box)({
  backgroundColor: '#f5f5f5',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  padding: '12px',
  minHeight: '80px',
  fontSize: '14px',
  color: '#444',
  lineHeight: 1.5,
  wordBreak: 'break-word',
  boxSizing: 'border-box',
});

const FieldLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: '13px',
  color: '#333',
  marginTop: '16px',
  marginBottom: '8px',
  borderBottom: '1px solid #e0e0e0',
  paddingBottom: '6px',
});

// ── Memoized question row ─────────────────────────────────────────────────────

interface QuestionTranslationRowProps {
  qIdx: number;
  srcQ: FbeQuestion | undefined;
  draftQ: FbeQuestion;
  fromLang: string;
  toLang: string;
  readOnly: boolean;
  showQuestion: boolean;
  showAdditionalInfo: boolean;
  onFieldChange: (qIdx: number, field: 'question' | 'questionShort' | 'additionalInfo', value: string) => void;
  onAnswerChange: (qIdx: number, aIdx: number, value: string) => void;
  onTableFieldChange: (qIdx: number, field: 'columnShort' | 'rowShort', value: string) => void;
  onRowPositionNameChange: (qIdx: number, posIdx: number, value: string) => void;
}

const QuestionTranslationRow = memo(function QuestionTranslationRow({
  qIdx,
  srcQ: origQ,
  draftQ: q,
  fromLang,
  toLang,
  readOnly,
  showQuestion,
  showAdditionalInfo,
  onFieldChange,
  onAnswerChange,
  onTableFieldChange,
  onRowPositionNameChange,
}: QuestionTranslationRowProps) {
  const srcQText = getLocalisedText(origQ?.question || [], fromLang);
  const tgtQText = getLocalisedText(q.question, toLang);
  const srcShort = origQ?.questionShort ? getLocalisedText(origQ.questionShort, fromLang) : '';
  const tgtShort = q.questionShort ? getLocalisedText(q.questionShort, toLang) : '';
  const srcInfo = origQ?.additionalInfo ? getLocalisedText(origQ.additionalInfo, fromLang) : '';
  const tgtInfo = q.additionalInfo ? getLocalisedText(q.additionalInfo, toLang) : '';
  return (
    <Box sx={{ mb: 3, pl: 2, borderLeft: '3px solid #e0e0e0' }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555', mb: 1 }}>
        {srcShort ? `Q${qIdx + 1} — ${srcShort}` : `Q${qIdx + 1}`}
      </Typography>
      {showQuestion && (
        <SideBySideRow>
          <SourceBox>
            {srcQText ? (
              <span dangerouslySetInnerHTML={{ __html: srcQText }} />
            ) : (
              <em style={{ color: '#aaa' }}>No text in source language</em>
            )}
          </SourceBox>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={tgtQText}
            onChange={(e) => onFieldChange(qIdx, 'question', e.target.value)}
            disabled={readOnly}
            sx={{ backgroundColor: 'white', '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
            inputProps={{ 'aria-label': `Q${qIdx + 1} translation` }}
          />
        </SideBySideRow>
      )}
      {showQuestion && (srcShort || tgtShort || origQ?.questionShort) && (
        <>
          <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Question Short</Typography>
          <SideBySideRow>
            <SourceBox sx={{ minHeight: '40px' }}>{srcShort || <em style={{ color: '#aaa' }}>—</em>}</SourceBox>
            <TextField
              size="small" fullWidth value={tgtShort}
              onChange={(e) => onFieldChange(qIdx, 'questionShort', e.target.value)}
              disabled={readOnly} sx={{ backgroundColor: 'white' }}
            />
          </SideBySideRow>
        </>
      )}
      {showAdditionalInfo && (
        <>
          <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Additional info</Typography>
          <SideBySideRow sx={{ alignItems: 'flex-start' }}>
            <SourceBox>
              {srcInfo ? <span dangerouslySetInnerHTML={{ __html: srcInfo }} /> : <em style={{ color: '#aaa' }}>—</em>}
            </SourceBox>
            <Box sx={{ '& .ProseMirror': { minHeight: '56px !important' } }}>
              <RichTextField
                labelText=""
                labelId={`q-additional-info-${qIdx}`}
                mandatory={false}
                helpTextTitle=""
                helpTextBody=""
                onChange={(val) => onFieldChange(qIdx, 'additionalInfo', val)}
                value={tgtInfo}
                disabled={readOnly}
                error={false}
                helperText=""
                onLock={() => {}}
                disabledLock={true}
                displayedLock={false}
              />
            </Box>
          </SideBySideRow>
        </>
      )}
      {q.answers && q.answers.length > 0 && (
        <>
          <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Answers</Typography>
          {q.answers.map((ans, aIdx) => {
            const srcAns = getLocalisedText(origQ?.answers?.[aIdx]?.answer || [], fromLang);
            const tgtAns = getLocalisedText(ans.answer, toLang);
            return (
              <SideBySideRow key={aIdx}>
                <SourceBox sx={{ minHeight: '40px' }}>
                  {srcAns || <em style={{ color: '#aaa' }}>—</em>}
                </SourceBox>
                <TextField
                  size="small" fullWidth value={tgtAns}
                  onChange={(e) => onAnswerChange(qIdx, aIdx, e.target.value)}
                  disabled={readOnly} sx={{ backgroundColor: 'white' }}
                />
              </SideBySideRow>
            );
          })}
        </>
      )}
      {q.questionType === FbeQuestionType.NUMBER_TABLE && (
        <>
          {(origQ?.rowShort || q.rowShort) && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Table row name</Typography>
              <SideBySideRow>
                <SourceBox sx={{ minHeight: '40px' }}>
                  {origQ?.rowShort ? getLocalisedText(origQ.rowShort, fromLang) || <em style={{ color: '#aaa' }}>—</em> : <em style={{ color: '#aaa' }}>—</em>}
                </SourceBox>
                <TextField
                  size="small" fullWidth
                  value={q.rowShort ? getLocalisedText(q.rowShort, toLang) : ''}
                  onChange={(e) => onTableFieldChange(qIdx, 'rowShort', e.target.value)}
                  disabled={readOnly} sx={{ backgroundColor: 'white' }}
                />
              </SideBySideRow>
            </>
          )}
          {(origQ?.columnShort || q.columnShort) && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Table column name</Typography>
              <SideBySideRow>
                <SourceBox sx={{ minHeight: '40px' }}>
                  {origQ?.columnShort ? getLocalisedText(origQ.columnShort, fromLang) || <em style={{ color: '#aaa' }}>—</em> : <em style={{ color: '#aaa' }}>—</em>}
                </SourceBox>
                <TextField
                  size="small" fullWidth
                  value={q.columnShort ? getLocalisedText(q.columnShort, toLang) : ''}
                  onChange={(e) => onTableFieldChange(qIdx, 'columnShort', e.target.value)}
                  disabled={readOnly} sx={{ backgroundColor: 'white' }}
                />
              </SideBySideRow>
            </>
          )}
          {q.rowPositionNames && q.rowPositionNames.length > 0 && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#888', mb: 0.5 }}>Row position names</Typography>
              {q.rowPositionNames.map((rpn, posIdx) => {
                const srcRpn = origQ?.rowPositionNames?.[posIdx];
                const srcText = srcRpn ? getLocalisedText(srcRpn.name, fromLang) : '';
                const tgtText = getLocalisedText(rpn.name, toLang);
                return (
                  <SideBySideRow key={posIdx}>
                    <SourceBox sx={{ minHeight: '40px' }}>
                      {srcText ? `${rpn.position}. ${srcText}` : <em style={{ color: '#aaa' }}>{`Position ${rpn.position}`}</em>}
                    </SourceBox>
                    <TextField
                      size="small" fullWidth
                      placeholder={`Position ${rpn.position}`}
                      value={tgtText}
                      onChange={(e) => onRowPositionNameChange(qIdx, posIdx, e.target.value)}
                      disabled={readOnly} sx={{ backgroundColor: 'white' }}
                    />
                  </SideBySideRow>
                );
              })}
            </>
          )}
        </>
      )}
    </Box>
  );
});

// ── Props ─────────────────────────────────────────────────────────────────────

interface SectionTranslationsTabProps {
  /** The single section being translated (current draft — used as source display). */
  section: FbeSection;
  /** Original saved section — used ONLY for save merge to preserve source entries. */
  originalSection: FbeSection;
  /** Form-level base/source language code (e.g. 'fi', 'en'). */
  defaultLanguage?: string;
  supportedLanguages: string[];
  frontline?: FbeFrontline;
  onSave: (updatedSection: FbeSection) => void;
  readOnly?: boolean;
  selectedLanguage?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SectionTranslationsTab({
  section,
  originalSection,
  defaultLanguage,
  supportedLanguages,
  frontline,
  onSave,
  readOnly = false,
  selectedLanguage,
}: SectionTranslationsTabProps): JSX.Element {
  const langDisplayNames = frontline?.settings.languageDisplayNames || {};
  const langLabel = (code: string) => getLanguageDisplayName(code, langDisplayNames);

  // Prefer form-level defaultLanguage, then section's first name entry
  const defaultLang =
    defaultLanguage ||
    section.name?.[0]?.language ||
    originalSection.name?.[0]?.language ||
    supportedLanguages[0] ||
    'en';

  const [fromLang, setFromLang] = useState<string>(defaultLang);
  // Use selectedLanguage as initial target when coming from the translations submenu.
  // Guard: if only one language is supported, toLang must equal fromLang — the tab
  // renders as read-only in that case, so no edits are lost.
  const [toLang, setToLang] = useState<string>(
    (selectedLanguage && selectedLanguage !== defaultLang)
      ? selectedLanguage
      : supportedLanguages.find((l) => l !== defaultLang) || defaultLang,
  );
  const targetLanguages = useMemo(
    () => supportedLanguages.filter((lang) => lang !== fromLang),
    [fromLang, supportedLanguages],
  );

  // Sync toLang when the parent localization dropdown changes.
  // Never allow toLang to equal fromLang unless there is truly no other option.
  useEffect(() => {
    if (
      selectedLanguage &&
      selectedLanguage !== toLang &&
      selectedLanguage !== fromLang
    ) {
      setToLang(selectedLanguage);
    }

  }, [selectedLanguage, fromLang]);

  // ── Draft state (section-level) ───────────────────────────────────────────
  const [draftSection, setDraftSection] = useState<FbeSection>(
    () => JSON.parse(JSON.stringify(section)),
  );

  // Re-sync drafts when the incoming section changes (e.g. after a save)
  useEffect(() => {
    setDraftSection(JSON.parse(JSON.stringify(section)));
  }, [section]);

  // Dirty: true when draftSection differs from the current section prop
  const isDirty = useMemo(
    () => JSON.stringify(draftSection) !== JSON.stringify(section),
    [draftSection, section],
  );

  // ── Language selectors ────────────────────────────────────────────────────
  const handleFromChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const newFrom = e.target.value;
      setFromLang(newFrom);
      if (toLang === newFrom) {
        const next = supportedLanguages.find((l) => l !== newFrom);
        if (next) setToLang(next);
      }
    },
    [toLang, supportedLanguages],
  );
  const handleToChange = useCallback(
    (e: SelectChangeEvent<string>) => setToLang(e.target.value),
    [],
  );

  // ── Field updaters ────────────────────────────────────────────────────────

  /** Update a LocalisedText[] field on the section itself (name, descriptions). */
  const updateSectionLTField = useCallback(
    (field: 'name' | 'descriptions', value: string) => {
      setDraftSection((prev) => ({
        ...prev,
        [field]: upsertLocalisedText(
          (prev[field] as LocalisedText[]) || [],
          toLang,
          value,
        ),
      }));
    },
    [toLang],
  );

  /** Update the mergeMetaData navigation text. */
  const updateNavText = useCallback(
    (value: string) => {
      setDraftSection((prev) => ({
        ...prev,
        mergeMetaData: {
          description: upsertLocalisedText(
            prev.mergeMetaData?.description || [],
            toLang,
            value,
          ),
          questionSetId: prev.mergeMetaData?.questionSetId || '',
          parentSetId: prev.mergeMetaData?.parentSetId || '',
          tacoRole: prev.mergeMetaData?.tacoRole || [],
          tag: prev.mergeMetaData?.tag || '',
        },
      }));
    },
    [toLang],
  );

  /** Update the loop name (only when looping is enabled). */
  const updateLoopName = useCallback(
    (value: string) => {
      setDraftSection((prev) => ({
        ...prev,
        loop: prev.loop
          ? { ...prev.loop, name: upsertLocalisedText(prev.loop.name || [], toLang, value) }
          : prev.loop,
      }));
    },
    [toLang],
  );

  /** Update a LocalisedText[] field on a specific question. */
  const updateQuestionField = useCallback(
    (qIdx: number, field: 'question' | 'questionShort' | 'additionalInfo', value: string) => {
      setDraftSection((prev) => {
        const questions = prev.questions.slice();
        const q = questions[qIdx];
        if (!q) return prev;
        const qCopy = { ...q } as FbeQuestion;
        (qCopy as unknown as Record<string, LocalisedText[]>)[field] = upsertLocalisedText(
          (qCopy[field] as LocalisedText[] | undefined) || [],
          toLang,
          value,
        );
        questions[qIdx] = qCopy;
        return { ...prev, questions };
      });
    },
    [toLang],
  );

  /** Update an answer text on a specific question. */
  const updateAnswerField = useCallback(
    (qIdx: number, aIdx: number, value: string) => {
      setDraftSection((prev) => {
        const questions = prev.questions.slice();
        const q = questions[qIdx];
        if (!q || !q.answers) return prev;
        const answers = q.answers.slice();
        const a = answers[aIdx];
        if (!a) return prev;
        answers[aIdx] = { ...a, answer: upsertLocalisedText(a.answer || [], toLang, value) };
        questions[qIdx] = { ...q, answers };
        return { ...prev, questions };
      });
    },
    [toLang],
  );

  /** Update columnShort or rowShort on a NUMBER_TABLE question. */
  const updateTableField = useCallback(
    (qIdx: number, field: 'columnShort' | 'rowShort', value: string) => {
      setDraftSection((prev) => {
        const questions = prev.questions.slice();
        const q = questions[qIdx];
        if (!q) return prev;
        const qCopy = { ...q } as FbeQuestion;
        (qCopy as unknown as Record<string, LocalisedText[]>)[field] = upsertLocalisedText(
          (qCopy[field] as LocalisedText[] | undefined) || [],
          toLang,
          value,
        );
        questions[qIdx] = qCopy;
        return { ...prev, questions };
      });
    },
    [toLang],
  );

  /** Update a row position name on a NUMBER_TABLE question. */
  const updateRowPositionName = useCallback(
    (qIdx: number, posIdx: number, value: string) => {
      setDraftSection((prev) => {
        const questions = prev.questions.slice();
        const q = questions[qIdx];
        if (!q || !q.rowPositionNames) return prev;
        const rowPositionNames = q.rowPositionNames.slice();
        const rpn = rowPositionNames[posIdx];
        if (!rpn) return prev;
        rowPositionNames[posIdx] = {
          ...rpn,
          name: upsertLocalisedText(rpn.name || [], toLang, value),
        };
        questions[qIdx] = { ...q, rowPositionNames };
        return { ...prev, questions };
      });
    },
    [toLang],
  );

  const [questionFilter, setQuestionFilter] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterUntranslated, setFilterUntranslated] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showSections, setShowSections] = useState(true);
  const [showQuestions, setShowQuestions] = useState(true);
  const [showAdditionalInfos, setShowAdditionalInfos] = useState(true);

  const allStringTypes = showSections && showQuestions && showAdditionalInfos;
  const someStringTypes = (showSections || showQuestions || showAdditionalInfos) && !allStringTypes;

  const handleAllStringTypes = (checked: boolean) => {
    setShowSections(checked);
    setShowQuestions(checked);
    setShowAdditionalInfos(checked);
  };

  // ── Completion ────────────────────────────────────────────────────────────
  const draftCompletion = useMemo(() => {
    const fields: string[] = [getLocalisedText(draftSection.name, toLang)];

    // Use section (current draft/edits) as source of truth for what fields exist
    const srcDesc =
      getLocalisedText(section.descriptions || [], fromLang) ||
      section.description ||
      '';
    if (hasMeaningfulText(srcDesc)) {
      fields.push(getLocalisedText(draftSection.descriptions || [], toLang));
    }

    const srcNav = getLocalisedText(section.mergeMetaData?.description || [], fromLang);
    if (hasMeaningfulText(srcNav)) {
      fields.push(getLocalisedText(draftSection.mergeMetaData?.description || [], toLang));
    }

    const srcLoopName = getLocalisedText(section.loop?.name || [], fromLang);
    if (hasMeaningfulText(srcLoopName)) {
      fields.push(getLocalisedText(draftSection.loop?.name || [], toLang));
    }

    draftSection.questions.forEach((q, qIdx) => {
      const srcQ = section.questions[qIdx];
      fields.push(getLocalisedText(q.question, toLang));
      if (srcQ?.questionShort) fields.push(getLocalisedText(q.questionShort || [], toLang));
      if (srcQ?.additionalInfo) fields.push(getLocalisedText(q.additionalInfo || [], toLang));
      if (srcQ?.answers)
        srcQ.answers.forEach((_, aIdx) =>
          fields.push(getLocalisedText(q.answers?.[aIdx]?.answer || [], toLang)),
        );
      if (srcQ?.columnShort) fields.push(getLocalisedText(q.columnShort || [], toLang));
      if (srcQ?.rowShort) fields.push(getLocalisedText(q.rowShort || [], toLang));
      if (srcQ?.rowPositionNames)
        srcQ.rowPositionNames.forEach((_, posIdx) =>
          fields.push(getLocalisedText(q.rowPositionNames?.[posIdx]?.name || [], toLang)),
        );
    });

    const filled = fields.filter(hasMeaningfulText).length;
    return fields.length > 0 ? Math.round((filled / fields.length) * 100) : 0;
  }, [draftSection, section, toLang, fromLang]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = () => {
    const mergeLT = (draft: LocalisedText[], orig: LocalisedText[]): LocalisedText[] => {
      const srcEntry = orig.find((t) => t.language === fromLang);
      const rest = draft.filter((t) => t.language !== fromLang);
      return srcEntry ? [srcEntry, ...rest] : draft;
    };

    const origDescriptions =
      originalSection.descriptions ||
      (originalSection.description
        ? [{ language: fromLang, text: originalSection.description }]
        : []);

    const mergedSection: FbeSection = {
      ...draftSection,
      name: mergeLT(draftSection.name, originalSection.name),
      descriptions: mergeLT(draftSection.descriptions || [], origDescriptions),
      ...(draftSection.mergeMetaData && {
        mergeMetaData: {
          ...draftSection.mergeMetaData,
          description: mergeLT(
            draftSection.mergeMetaData.description || [],
            originalSection.mergeMetaData?.description || [],
          ),
        },
      }),
      ...(draftSection.loop && originalSection.loop && {
        loop: {
          ...draftSection.loop,
          name: mergeLT(draftSection.loop.name || [], originalSection.loop.name || []),
        },
      }),
      questions: draftSection.questions.map((draftQ) => {
        const origQ =
          originalSection.questions.find((q) => q.questionId === draftQ.questionId) ??
          originalSection.questions.find((_, i) => i === draftSection.questions.indexOf(draftQ));
        return {
          ...draftQ,
          question: mergeLT(draftQ.question, origQ?.question || []),
          ...(draftQ.questionShort && {
            questionShort: mergeLT(draftQ.questionShort, origQ?.questionShort || []),
          }),
          ...(draftQ.additionalInfo && {
            additionalInfo: mergeLT(draftQ.additionalInfo, origQ?.additionalInfo || []),
          }),
          ...(draftQ.answers && {
            answers: draftQ.answers.map((a, aIdx) => ({
              ...a,
              answer: mergeLT(a.answer, origQ?.answers?.[aIdx]?.answer || []),
            })),
          }),
          ...(draftQ.columnShort && {
            columnShort: mergeLT(draftQ.columnShort, origQ?.columnShort || []),
          }),
          ...(draftQ.rowShort && {
            rowShort: mergeLT(draftQ.rowShort, origQ?.rowShort || []),
          }),
          ...(draftQ.rowPositionNames && {
            rowPositionNames: draftQ.rowPositionNames.map((rpn, posIdx) => ({
              ...rpn,
              name: mergeLT(rpn.name, origQ?.rowPositionNames?.[posIdx]?.name || []),
            })),
          }),
        };
      }),
    };

    onSave(mergedSection);
  };

  // ── Render helpers — source from `section` (current edits), target from `draftSection` ─
  const srcName = getLocalisedText(section.name, fromLang);
  const tgtName = getLocalisedText(draftSection.name, toLang);

  const srcDesc =
    getLocalisedText(section.descriptions || [], fromLang) ||
    section.description ||
    '';
  const tgtDesc = getLocalisedText(draftSection.descriptions || [], toLang);

  const srcNav = getLocalisedText(section.mergeMetaData?.description || [], fromLang);
  const tgtNav = getLocalisedText(draftSection.mergeMetaData?.description || [], toLang);

  const hasLoop = Boolean(section.loop);
  const srcLoopName = getLocalisedText(section.loop?.name || [], fromLang);
  const tgtLoopName = getLocalisedText(draftSection.loop?.name || [], toLang);

  return (
    <Box sx={{
      padding: '24px',
      overflowY: 'auto',
      maxHeight: '100%',
      '&::-webkit-scrollbar': { width: '8px' },
      '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
      '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
      '&::-webkit-scrollbar-thumb:hover': { background: '#999' },
    }}>
      {/* ── Language selectors + badge ── */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ fontSize: '12px', mb: 0.5 }}>From (source)</FormLabel>
          <Select
            value={fromLang}
            onChange={handleFromChange}
            inputProps={{ 'aria-label': 'Source language' }}
          >
            {supportedLanguages.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {langLabel(lang)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ fontSize: '12px', mb: 0.5 }}>To (target)</FormLabel>
          <Select
            value={toLang}
            onChange={handleToChange}
            displayEmpty
            disabled={targetLanguages.length === 0}
            inputProps={{ 'aria-label': 'Target language' }}
          >
            <MenuItem value="" disabled>
              <em style={{ color: '#aaa' }}>Select language</em>
            </MenuItem>
            {/* Inject the current target language when it is filtered out by source selection */}
            {toLang && !targetLanguages.includes(toLang) && (
              <MenuItem value={toLang}>{langLabel(toLang)}</MenuItem>
            )}
            {targetLanguages.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {langLabel(lang)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={2.5}>
          <Chip
            icon={<TranslateOutlined fontSize="small" />}
            label={`${draftCompletion}% translated`}
            color={
              draftCompletion === 100
                ? 'success'
                : draftCompletion > 0
                ? 'warning'
                : 'default'
            }
            variant="outlined"
            size="small"
          />
        </Box>

        {/* ── Search + filter icons ── */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
          {searchOpen && (
            <TextField
              size="small"
              placeholder="Search questions…"
              value={questionFilter}
              autoFocus
              onChange={(e) => setQuestionFilter(e.target.value)}
              sx={{ width: 200 }}
            />
          )}
          <IconButton
            size="small"
            onClick={() => { setSearchOpen((o) => !o); if (searchOpen) setQuestionFilter(''); }}
            color={searchOpen ? 'primary' : 'default'}
            title="Search questions"
          >
            <SearchOutlined />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            color={(filterUntranslated || !allStringTypes) ? 'primary' : 'default'}
            title="Filter"
          >
            <FilterAltOutlined />
          </IconButton>
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 2, minWidth: 220, borderRadius: 2, boxShadow: 4 } }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '15px', mb: 1.5 }}>Show</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterUntranslated}
                    onChange={(e) => setFilterUntranslated(e.target.checked)}
                    size="small"
                  />
                }
                label="Only untranslated"
              />
            </FormGroup>
            <Divider sx={{ my: 1 }} />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allStringTypes}
                    indeterminate={someStringTypes}
                    onChange={(e) => handleAllStringTypes(e.target.checked)}
                    size="small"
                  />
                }
                label="All string types"
              />
              <Box sx={{ pl: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSections}
                      onChange={(e) => setShowSections(e.target.checked)}
                      size="small"
                      sx={{ color: '#bbb', '&.Mui-checked': { color: '#bbb' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#888', fontSize: '14px' }}>Sections</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showQuestions}
                      onChange={(e) => setShowQuestions(e.target.checked)}
                      size="small"
                      sx={{ color: '#bbb', '&.Mui-checked': { color: '#bbb' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#888', fontSize: '14px' }}>Questions</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAdditionalInfos}
                      onChange={(e) => setShowAdditionalInfos(e.target.checked)}
                      size="small"
                      sx={{ color: '#bbb', '&.Mui-checked': { color: '#bbb' } }}
                    />
                  }
                  label={<Typography sx={{ color: '#888', fontSize: '14px' }}>Additional infos</Typography>}
                />
              </Box>
            </FormGroup>
          </Popover>
        </Box>
      </Box>

      {/* ── Column headers ── */}
      <SideBySideRow>
        <ColumnHeader>{langLabel(fromLang)} (source)</ColumnHeader>
        <ColumnHeader>{langLabel(toLang)} (target)</ColumnHeader>
      </SideBySideRow>

      {/* ── Section Name ── */}
      {showSections && !(filterUntranslated && (!hasMeaningfulText(srcName) || hasMeaningfulText(tgtName))) &&
        (!questionFilter.trim() || [srcName, tgtName].some((t) => t.toLowerCase().includes(questionFilter.trim().toLowerCase()))) && (
        <>
          <FieldLabel>Section Name</FieldLabel>
          <SideBySideRow>
            <SourceBox>
              {srcName || <em style={{ color: '#aaa' }}>No text in source language</em>}
            </SourceBox>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={tgtName}
              onChange={(e) => updateSectionLTField('name', e.target.value)}
              disabled={readOnly}
              sx={{ backgroundColor: 'white', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
              inputProps={{ 'aria-label': 'Section name translation' }}
            />
          </SideBySideRow>
        </>
      )}

      {/* ── Section Description (always shown) ── */}
      {showSections && !(filterUntranslated && (!hasMeaningfulText(srcDesc) || hasMeaningfulText(tgtDesc))) &&
        (!questionFilter.trim() || [srcDesc, tgtDesc].some((t) => t.toLowerCase().includes(questionFilter.trim().toLowerCase()))) && (
        <>
          <FieldLabel>Section Description</FieldLabel>
          <SideBySideRow>
            {hasMeaningfulText(srcDesc) ? (
              <SourceBox
                dangerouslySetInnerHTML={{ __html: srcDesc }}
                sx={{ '& p': { margin: '4px 0' } }}
              />
            ) : (
              <SourceBox><em style={{ color: '#aaa' }}>No description in source language</em></SourceBox>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', '& .ProseMirror': { minHeight: '56px !important' } }}>
              <RichTextField
                labelText=""
                labelId="section-desc-translation"
                mandatory={false}
                helpTextTitle=""
                helpTextBody=""
                onChange={(val) => updateSectionLTField('descriptions', val)}
                value={tgtDesc}
                disabled={readOnly}
                error={false}
                helperText=""
                onLock={() => {}}
                disabledLock={true}
                displayedLock={false}
              />
            </Box>
          </SideBySideRow>
        </>
      )}

      {/* ── Navigation Text (only if source has content) ── */}
      {showSections && hasMeaningfulText(srcNav) && !(filterUntranslated && hasMeaningfulText(tgtNav)) &&
        (!questionFilter.trim() || [srcNav, tgtNav].some((t) => t.toLowerCase().includes(questionFilter.trim().toLowerCase()))) && (
        <>
          <FieldLabel>Navigation Text</FieldLabel>
          <SideBySideRow>
            <SourceBox>{srcNav}</SourceBox>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={tgtNav}
              onChange={(e) => updateNavText(e.target.value)}
              disabled={readOnly}
              sx={{ backgroundColor: 'white', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
              inputProps={{ 'aria-label': 'Navigation text translation' }}
            />
          </SideBySideRow>
        </>
      )}

      {/* ── Loop Name (only when looping is enabled) ── */}
      {showSections && hasLoop && !(filterUntranslated && (!hasMeaningfulText(srcLoopName) || hasMeaningfulText(tgtLoopName))) &&
        (!questionFilter.trim() || [srcLoopName, tgtLoopName].some((t) => t.toLowerCase().includes(questionFilter.trim().toLowerCase()))) && (
        <>
          <FieldLabel>Loop Name</FieldLabel>
          <SideBySideRow>
            <SourceBox>
              {srcLoopName || <em style={{ color: '#aaa' }}>No loop name in source language</em>}
            </SourceBox>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={tgtLoopName}
              onChange={(e) => updateLoopName(e.target.value)}
              disabled={readOnly}
              sx={{ backgroundColor: 'white', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
              inputProps={{ 'aria-label': 'Loop name translation' }}
            />
          </SideBySideRow>
        </>
      )}

      {/* ── Questions ── */}
      {(showQuestions || showAdditionalInfos) && section.questions.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '14px', mb: 1 }}>
            Questions ({section.questions.length})
          </Typography>
          {(() => {
            const term = questionFilter.trim().toLowerCase();
            // Helper: search across all language entries in a LocalisedText array
            const anyTextIncludes = (lt: LocalisedText[] | undefined) =>
              (lt || []).some((t) => (t.text || '').toLowerCase().includes(term));
            const rows = draftSection.questions
              .map((draftQ, qIdx) => ({
                draftQ,
                qIdx,
                srcQ: section.questions.find((q) => q.questionId === draftQ.questionId) ?? section.questions[qIdx],
              }))
              .sort((a, b) => a.draftQ.order - b.draftQ.order)
              .filter(({ draftQ, srcQ }) => {
                if (term) {
                  const matchesText =
                    anyTextIncludes(srcQ?.question) ||
                    anyTextIncludes(srcQ?.questionShort) ||
                    anyTextIncludes(srcQ?.additionalInfo) ||
                    (srcQ?.answers || []).some((a) => anyTextIncludes(a.answer));
                  if (!matchesText) return false;
                }
                if (filterUntranslated) {
                  // Only count a field as "needing translation" when its row type is
                  // actually visible — so the user sees something actionable.
                  const qSrc = showQuestions && hasMeaningfulText(getLocalisedText(srcQ?.question || [], fromLang));
                  const qDone = !qSrc || hasMeaningfulText(getLocalisedText(draftQ.question, toLang));

                  const shortSrc = showQuestions && hasMeaningfulText(getLocalisedText(srcQ?.questionShort || [], fromLang));
                  const shortDone = !shortSrc || hasMeaningfulText(getLocalisedText(draftQ.questionShort || [], toLang));

                  const infoSrc = showAdditionalInfos && hasMeaningfulText(getLocalisedText(srcQ?.additionalInfo || [], fromLang));
                  const infoDone = !infoSrc || hasMeaningfulText(getLocalisedText(draftQ.additionalInfo || [], toLang));

                  const answersDone = (srcQ?.answers || []).every((srcAns, aIdx) => {
                    const srcAnsHasText = hasMeaningfulText(getLocalisedText(srcAns.answer || [], fromLang));
                    const tgtAnsHasText = hasMeaningfulText(getLocalisedText(draftQ.answers?.[aIdx]?.answer || [], toLang));
                    return !srcAnsHasText || tgtAnsHasText;
                  });

                  if (qDone && shortDone && infoDone && answersDone) return false;
                }
                return true;
              });
            if (rows.length === 0) {
              return (
                <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                  {filterUntranslated
                    ? 'All questions are translated.'
                    : term
                    ? `No questions match "${questionFilter}"`
                    : ''}
                </Typography>
              );
            }
            return rows.map(({ draftQ, qIdx, srcQ }) => (
              <QuestionTranslationRow
                key={qIdx}
                qIdx={qIdx}
                srcQ={srcQ}
                draftQ={draftQ}
                fromLang={fromLang}
                toLang={toLang}
                readOnly={readOnly}
                showQuestion={showQuestions}
                showAdditionalInfo={showAdditionalInfos}
                onFieldChange={updateQuestionField}
                onAnswerChange={updateAnswerField}
                onTableFieldChange={updateTableField}
                onRowPositionNameChange={updateRowPositionName}
              />
            ));
          })()}
        </Box>
      )}

      {/* ── Save ── */}
      {!readOnly && (
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isDirty}
            sx={{ borderRadius: '24px', padding: '6px 50px' }}
          >
            Save Translations
          </Button>
        </Box>
      )}
    </Box>
  );
}